import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactSortable } from "react-sortablejs";
import { adminService } from '../services/adminApi';
import { Search, AlertCircle, CheckCircle, Loader2, FileStack, SquareCheck, Square, X, GripVertical } from 'lucide-react';
import { pdfMergeService } from '../utils/pdfWorkerUtils';

const SongAssembler = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Selection State
  const [selectedSlugs, setSelectedSlugs] = useState(new Set());
  const [selectedSongs, setSelectedSongs] = useState([]); // Ordered List for Merge
  const [isMerging, setIsMerging] = useState(false);
  
  // Merge Config State
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeFilename, setMergeFilename] = useState('');

  // Handle Escape Key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowMergeModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleSelection = (song) => {
    const slug = song.song_slug;
    
    // 1. Update Lookup Set (Source of Truth for Selection)
    setSelectedSlugs(prev => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });

    // 2. Update Ordered Array (Prevent Duplicates)
    setSelectedSongs(prev => {
      const exists = prev.some(s => s.song_slug === slug);
      if (exists) {
        return prev.filter(s => s.song_slug !== slug);
      } else {
        return [...prev, song];
      }
    });
  };

  const handleSearch = async (e) => {
    const val = e.target.value;
    setQuery(val);
    if (message) setMessage(null);

    if (val.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const data = await adminService.searchSongs(val);
      setResults(data);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.status === 401 
        ? 'Unauthorized. Please sign in.' 
        : 'Search failed. Check backend connection.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const openMergeModal = () => {
    if (selectedSongs.length < 2) {
      setMessage({ type: 'error', text: 'Select at least 2 songs to merge.' });
      return;
    }
    setMergeFilename(`merged_scores_${new Date().toISOString().slice(0,10)}`);
    setShowMergeModal(true);
  };

  const handleExecuteMerge = async () => {
    setIsMerging(true);
    setMessage({ type: 'info', text: `Merging ${selectedSongs.length} songs...` });
    setShowMergeModal(false);

    try {
      const filesToMerge = [];
      const mergeJobs = [];

      for (const song of selectedSongs) {
        const songDetail = await adminService.getSongDetail(song.song_slug);
        const scoreFile = songDetail.media_files.find(m => m.category === 'scores' || m.category === 'score');
        
        if (scoreFile) {
          const fileData = await adminService.downloadFileProxy(scoreFile.url);
          const fileName = `${songDetail.title}_${song.song_slug}.pdf`;
          
          filesToMerge.push({
            name: fileName,
            data: fileData
          });

          mergeJobs.push({
            fileName: fileName,
            rangeType: 'all'
          });
        }
      }

      if (filesToMerge.length < 2) {
        throw new Error('At least 2 songs with sheet music are required for merging.');
      }

      const mergedPdfBytes = await pdfMergeService.merge(filesToMerge, mergeJobs);

      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${mergeFilename.endsWith('.pdf') ? mergeFilename : mergeFilename + '.pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setMessage({ type: 'success', text: `Successfully merged ${filesToMerge.length} songs!` });
      setSelectedSlugs(new Set());
      setSelectedSongs([]);
    } catch (err) {
      console.error('Merge error:', err);
      const errMsg = err.response?.data?.detail || err.message || 'Unknown error';
      setMessage({ type: 'error', text: `Merge failed: ${errMsg}` });
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Header & Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Merge Scores</h2>
          <p className="text-gray-500">Combine multiple scores into a single PDF.</p>
        </div>

        {selectedSlugs.size > 0 && (
          <div className="bg-blue-600 text-white p-3 rounded-2xl shadow-lg flex items-center gap-4 animate-fade-in">
            <div className="flex items-center gap-2 pl-2">
              <SquareCheck size={20} />
              <span className="font-bold whitespace-nowrap">{selectedSlugs.size} selected</span>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => { setSelectedSlugs(new Set()); setSelectedSongs([]); }}
                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-bold text-sm"
              >
                Clear
              </button>
              <button 
                onClick={openMergeModal}
                disabled={isMerging}
                className="px-3 py-1.5 bg-white text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-bold text-sm flex items-center gap-2"
              >
                {isMerging ? <Loader2 className="animate-spin" size={16} /> : <FileStack size={16} />}
                Merge
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search Input */}
      <div className="relative shadow-md rounded-2xl transition-all duration-300 focus-within:shadow-xl focus-within:-translate-y-1 bg-white border border-gray-100">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
          {loading ? <Loader2 className="animate-spin text-blue-500" size={24}/> : <Search size={24} />}
        </div>
        <input
          type="text"
          placeholder="Search by title or number (e.g. 'VIP 171')..."
          className="w-full pl-12 pr-4 py-4 bg-transparent border-none rounded-2xl focus:ring-0 text-lg text-gray-800 placeholder-gray-400"
          value={query}
          onChange={handleSearch}
        />
      </div>

      {message && (
        <div 
          className={`p-4 rounded-2xl flex items-center gap-3 text-sm font-bold shadow-sm animate-fade-in border ${
            message.type === 'success' 
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
              : message.type === 'info'
                ? 'bg-blue-50 text-blue-700 border-blue-100'
                : 'bg-red-50 text-red-700 border-red-100'
          }`}
        >
          <div className={`p-2 rounded-full ${message.type === 'success' ? 'bg-emerald-100' : 'bg-red-100'}`}>
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          </div>
          <span>{message.text}</span>
        </div>
      )}

      {/* Results Section */}
      <div className="space-y-4">
        {query.length > 0 && results.length === 0 && !loading ? (
          <div className="text-center py-12 opacity-50">
            <Search size={48} className="mx-auto mb-4 text-gray-300"/>
            <p className="text-lg text-gray-500">No songs found matching "{query}"</p>
          </div>
        ) : (
          results.map((song) => (
            <div 
              key={song.song_slug} 
              onClick={() => toggleSelection(song)}
              className={`group p-4 rounded-2xl border transition-all duration-300 flex items-center gap-4 cursor-pointer ${
                selectedSlugs.has(song.song_slug) 
                  ? 'bg-blue-50 border-blue-200 shadow-md ring-1 ring-blue-200' 
                  : 'bg-white border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-100'
              }`}
            >
              <div className={`flex-shrink-0 transition-colors ${selectedSlugs.has(song.song_slug) ? 'text-blue-600' : 'text-gray-300 group-hover:text-blue-400'}`}>
                {selectedSlugs.has(song.song_slug) ? <SquareCheck size={24} /> : <Square size={24} />}
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-gray-900 text-lg truncate group-hover:text-blue-700 transition-colors">
                  {song.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs font-bold text-gray-600 uppercase tracking-wide">
                    {song.collection} {song.track_number}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        
        {results.length === 0 && !query && (
          <div className="text-center py-20">
            <p className="text-gray-400">Start typing to search for scores to merge...</p>
          </div>
        )}
      </div>

      {/* Merge Configuration Modal (Portal) */}
      {showMergeModal && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in cursor-pointer"
          onClick={() => setShowMergeModal(false)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slide-up ring-1 ring-gray-900/5 cursor-default flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                  <FileStack size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Configure Score Merge</h3>
              </div>
              <button onClick={() => setShowMergeModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Filename Input */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Output Filename</label>
                <div className="flex items-center gap-2">
                  <input 
                    value={mergeFilename}
                    onChange={e => setMergeFilename(e.target.value)}
                    className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium font-mono text-sm"
                    placeholder="merged_score.pdf"
                  />
                  <span className="text-gray-400 font-bold">.pdf</span>
                </div>
              </div>

              {/* Sortable List */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Merge Order (Drag to Reorder)
                </label>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-2 max-h-64 overflow-y-auto">
                  <ReactSortable 
                    list={selectedSongs} 
                    setList={setSelectedSongs}
                    animation={200}
                    ghostClass="opacity-50"
                    className="space-y-2"
                  >
                    {selectedSongs.map((song, index) => (
                      <div key={song.song_slug} className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm flex items-center gap-3 cursor-move hover:border-blue-300 transition-colors group">
                        <div className="text-gray-300 group-hover:text-gray-500">
                          <GripVertical size={16} />
                        </div>
                        <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 truncate text-sm">{song.title}</p>
                          <p className="text-xs text-gray-500 truncate">{song.collection} {song.track_number}</p>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); toggleSelection(song); }}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </ReactSortable>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
              <button 
                onClick={() => setShowMergeModal(false)} 
                className="px-5 py-2.5 font-bold text-gray-600 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button 
                onClick={handleExecuteMerge} 
                className="px-6 py-2.5 font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
              >
                <FileStack size={18} /> Merge {selectedSongs.length} Files
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SongAssembler;
